import { useState } from "react";
import {
  IPHONE_SCREEN_PRICING,
  IPHONE_BATTERY_PRICING,
  IPHONE_CHARGING_PORT_PRICING,
  IPHONE_BACK_GLASS_PRICING,
  OTHER_REPAIRS,
  BUSINESS,
} from "@/data";
import { SectionHeading, CallButton } from "./Buttons";
import * as Icons from "lucide-react";

const TABS = [
  { key: "iphone_screen",   label: "iPhone Screen" },
  { key: "iphone_battery",  label: "iPhone Battery" },
  { key: "iphone_back",     label: "iPhone Back Glass" },
  { key: "iphone_port",     label: "Charging Port" },
  { key: "samsung",         label: "Samsung" },
  { key: "ipad",            label: "iPad / Tablet" },
  { key: "watch",           label: "Apple Watch" },
];

const Price = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="font-mono text-xs uppercase tracking-wider text-slate-400">N/A</span>;
  }
  if (typeof value === "string") {
    return <span className="font-display text-2xl font-black text-brand-navy">{value}</span>;
  }
  return <span className="font-display text-2xl font-black text-brand-navy">${value}</span>;
};

export default function RepairPricing() {
  const [active, setActive] = useState("iphone_screen");

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 md:py-28 px-4 md:px-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Transparent Pricing"
          title={<>Popular Repair Prices.<br /><span className="text-slate-400">No surprises.</span></>}
          sub="Final price may vary by model, part type, stock, and condition. iPhone screens come in two grades — INCELL (budget) and OLED (premium). Call to confirm today's price."
          testid="pricing-heading"
        />

        {/* Tabs */}
        <div className="mt-10 flex gap-2 overflow-x-auto no-scrollbar pb-2" role="tablist" data-testid="pricing-tabs">
          {TABS.map((t) => {
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                onClick={() => setActive(t.key)}
                data-testid={`pricing-tab-${t.key}`}
                className={`shrink-0 px-5 py-2.5 rounded-full font-display font-bold text-sm transition-all border ${
                  isActive
                    ? "bg-brand-red text-white border-brand-red glow-red"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-red/40 hover:text-brand-red"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {active === "iphone_screen" && <IPhoneScreenTable />}
          {active === "iphone_battery" && <SinglePriceGrid items={IPHONE_BATTERY_PRICING} type="battery" />}
          {active === "iphone_back" && <SinglePriceGrid items={IPHONE_BACK_GLASS_PRICING} type="back" />}
          {active === "iphone_port" && <SinglePriceGrid items={IPHONE_CHARGING_PORT_PRICING} type="port" />}
          {active === "samsung" && <CallForPriceGrid items={OTHER_REPAIRS.samsung.items} />}
          {active === "ipad" && <CallForPriceGrid items={OTHER_REPAIRS.ipad.items} />}
          {active === "watch" && <CallForPriceGrid items={OTHER_REPAIRS.watch.items} />}
        </div>

        {/* Footer note */}
        <div className="mt-10 bg-brand-navy text-white rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 shadow-xl">
          <div>
            <h4 className="font-display text-xl md:text-2xl font-extrabold">Don't see your model?</h4>
            <p className="text-sm text-white/70 mt-1">We repair almost every modern phone, tablet, and watch. Call us with your model.</p>
          </div>
          <CallButton testid="pricing-call-btn">Call {BUSINESS.phone}</CallButton>
        </div>
      </div>
    </section>
  );
}

function IPhoneScreenTable() {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden" data-testid="iphone-screen-table">
      {/* Header row */}
      <div className="grid grid-cols-12 px-5 md:px-7 py-4 bg-slate-50 border-b border-slate-200">
        <div className="col-span-6 md:col-span-7 text-[11px] md:text-xs font-bold uppercase tracking-widest text-slate-500">Model · newest first</div>
        <div className="col-span-3 md:col-span-2 text-[11px] md:text-xs font-bold uppercase tracking-widest text-slate-500 text-right">
          INCELL
          <div className="text-[9px] font-medium text-slate-400 tracking-wider normal-case">budget</div>
        </div>
        <div className="col-span-3 md:col-span-3 text-[11px] md:text-xs font-bold uppercase tracking-widest text-brand-red text-right">
          OLED ★
          <div className="text-[9px] font-medium text-brand-red/80 tracking-wider normal-case">recommended</div>
        </div>
      </div>

      {IPHONE_SCREEN_PRICING.map((row, i) => (
        <div
          key={row.model}
          data-testid={`iphone-screen-row-${i}`}
          className={`grid grid-cols-12 px-5 md:px-7 py-4 items-center border-b border-slate-100 last:border-b-0 transition-colors hover:bg-green-50/50`}
        >
          <div className="col-span-6 md:col-span-7">
            <div className="font-display font-extrabold text-brand-navy text-[15px] md:text-base leading-tight">{row.model}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 hidden md:block">{BUSINESS.warrantyDays}-day warranty · ~15 min</div>
          </div>
          <div className="col-span-3 md:col-span-2 text-right">
            <Price value={row.incell} />
          </div>
          <div className="col-span-3 md:col-span-3 text-right">
            <div className="inline-flex items-center gap-2">
              <Price value={row.oled} />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-brand-red mt-0.5">recommended</div>
          </div>
        </div>
      ))}

      <div className="px-5 md:px-7 py-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs text-slate-500">Prices may change based on part availability. Call to confirm today's price.</p>
        <a
          href={`tel:${BUSINESS.phoneTel}`}
          data-testid="iphone-screen-call"
          className="inline-flex items-center gap-2 text-xs font-bold text-brand-red hover:text-brand-red-dark"
        >
          <Icons.Phone className="w-3.5 h-3.5" />
          Call to Confirm
        </a>
      </div>
    </div>
  );
}

function SinglePriceGrid({ items, type }) {
  const labels = {
    battery: { time: "~30–40 min", note: "Health calibration included" },
    port: { time: "~45–60 min", note: "90-day warranty" },
    back: { time: "Same-day", note: "Most models" },
  };
  const info = labels[type] || { time: "Varies", note: "Call to confirm" };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid={`pricing-grid-${type}`}>
      {items.map((it, idx) => (
        <div
          key={`${it.model}-${idx}`}
          data-testid={`pricing-card-${type}-${idx}`}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm card-soft-hover p-6 group"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <h4 className="font-display font-extrabold text-lg text-brand-navy tracking-tight leading-tight">{it.model}</h4>
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-brand-red bg-green-50 border border-green-100 px-2 py-1 rounded-md">
              {BUSINESS.warrantyDays}-day Warranty
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display text-4xl font-black text-brand-navy tracking-tight">
              {typeof it.price === "string" && !it.price.includes("-") ? it.price : `$${it.price}`}
            </span>
            <span className="text-xs font-bold text-slate-500 uppercase">starting</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-5 flex-wrap">
            <Icons.Clock className="w-3.5 h-3.5 text-brand-blue" />
            <span className="font-semibold">{info.time}</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">{info.note}</span>
          </div>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid={`pricing-card-cta-${type}-${idx}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-brand-red hover:text-brand-red-dark group-hover:gap-3 transition-all"
          >
            <Icons.Phone className="w-4 h-4" />
            Call to Confirm Price
            <Icons.ArrowRight className="w-4 h-4" />
          </a>
        </div>
      ))}
    </div>
  );
}

function CallForPriceGrid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="pricing-grid-call">
      {items.map((it, idx) => (
        <div
          key={`${it.model}-${idx}`}
          data-testid={`pricing-card-call-${idx}`}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-md hover:border-brand-red/30 transition-all"
        >
          <h4 className="font-display font-extrabold text-lg text-brand-navy tracking-tight leading-tight mb-2">{it.model}</h4>
          <p className="text-sm text-slate-500 mb-5">Call for current pricing — varies by exact model and part availability.</p>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid={`pricing-card-call-cta-${idx}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-brand-red hover:text-brand-red-dark"
          >
            <Icons.Phone className="w-4 h-4" />
            Call for Price
            <Icons.ArrowRight className="w-4 h-4" />
          </a>
        </div>
      ))}
    </div>
  );
}
